import React from "react";
import styled from "styled-components";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

interface WeekNavigatorProps {
  periodLabel: string;
  isCurrentPeriod: boolean;
  onPrevious: () => void;
  onNext: () => void;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ArrowButton = styled.button<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${(props) => (props.$disabled ? "#e0e0e0" : "#bdbdbd")};
  border-radius: 6px;
  background: ${(props) => (props.$disabled ? "#fafafa" : "#ffffff")};
  color: ${(props) => (props.$disabled ? "#bdbdbd" : "#424242")};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: ${(props) => (props.$disabled ? "#fafafa" : "#f5f5f5")};
    border-color: ${(props) => (props.$disabled ? "#e0e0e0" : "#9e9e9e")};
  }
`;

const WeekLabel = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  min-width: 200px;
  text-align: center;
`;

// =============================================
// Displays the active period label with left and
// right arrow buttons for period navigation.
//
// The right arrow is visually disabled and
// non-interactive when isCurrentPeriod is true,
// preventing the user from stepping into the
// future where no data exists.
// =============================================
const WeekNavigator: React.FC<WeekNavigatorProps> = ({
  periodLabel,
  isCurrentPeriod,
  onPrevious,
  onNext,
}) => {
  const handleNextClick = (): void => {
    if (!isCurrentPeriod) {
      onNext();
    }
  };

  return (
    <Container>
      <ArrowButton
        $disabled={false}
        onClick={onPrevious}
        aria-label="Previous period"
        type="button"
      >
        <ArrowBackIosNewIcon style={{ fontSize: 14 }} />
      </ArrowButton>

      <WeekLabel>{periodLabel}</WeekLabel>

      <ArrowButton
        $disabled={isCurrentPeriod}
        onClick={handleNextClick}
        aria-label="Next period"
        aria-disabled={isCurrentPeriod}
        type="button"
      >
        <ArrowForwardIosIcon style={{ fontSize: 14 }} />
      </ArrowButton>
    </Container>
  );
};

export default WeekNavigator;
